import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {HTMLElement | null} */
  #accountButton = null;

  /** @type {Element | null} */
  #pageWrapper = null;

  /** @type {ResizeObserver | null} */
  #pageWrapperObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#watchAccountDialogInset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#accountButton?.removeEventListener('pointerdown', this.#syncAccountDialogInset);
    window.removeEventListener('resize', this.#syncAccountDialogInset);
    this.#pageWrapperObserver?.disconnect();
    this.#pageWrapperObserver = null;
  }

  /**
   * Keeps `--account-dialog-inset-end` — the inset `header-actions.liquid` feeds to
   * `shopify-account::part(dialog)` — aligned with the account button's inline-end edge.
   *
   * CSS anchor positioning would be the obvious tool, but the dialog is `position: fixed`
   * while its anchor sits in a sticky header inside `.page-wrapper`, the theme's scroll
   * container above 990px. Chromium then hit-tests the anchored dialog at a different offset
   * than it paints it, which left the top of the dialog's buttons unclickable once the page
   * was scrolled.
   */
  #watchAccountDialogInset() {
    const button = this.querySelector('.account-button');
    if (!(button instanceof HTMLElement)) return;

    this.#accountButton = button;
    this.#pageWrapper = document.querySelector('.page-wrapper');

    // Re-measure right before the dialog opens, and whenever the layout can have moved the
    // button. A drawer squeezing the page shifts the button without resizing it, so observe
    // the wrapper that does change size rather than the button itself.
    button.addEventListener('pointerdown', this.#syncAccountDialogInset);
    window.addEventListener('resize', this.#syncAccountDialogInset);

    if (this.#pageWrapper) {
      this.#pageWrapperObserver = new ResizeObserver(this.#syncAccountDialogInset);
      this.#pageWrapperObserver.observe(this.#pageWrapper);
    } else {
      this.#syncAccountDialogInset();
    }
  }

  /**
   * Measures the button's inline-end edge against `.page-wrapper` rather than the viewport on
   * purpose: while a drawer is open the stock rule in `base.css` already translates the dialog
   * by the drawer width, and a wrapper-relative inset leaves that compensation intact.
   *
   * Publishes a second inset for the Colmore login popover (`--account-dialog-inset-end-page`),
   * measured from this element's own inline-end edge — the last icon in the right cluster,
   * which sits on the page margin. Figma `4144:3996` pins that panel to the page margin rather
   * than to the account button, while the logged-in menu popover keeps the button alignment.
   */
  #syncAccountDialogInset = () => {
    const button = this.#accountButton;
    if (!button) return;

    const bounds = this.#pageWrapper?.getBoundingClientRect();
    const rtl = getComputedStyle(button).direction === 'rtl';
    const wrapperStart = bounds?.left ?? 0;
    const wrapperEnd = bounds?.right ?? document.documentElement.clientWidth;

    /** @param {DOMRect} rect */
    const insetFor = (rect) => Math.round(rtl ? rect.left - wrapperStart : wrapperEnd - rect.right);

    button.style.setProperty('--account-dialog-inset-end', `${insetFor(button.getBoundingClientRect())}px`);
    button.style.setProperty('--account-dialog-inset-end-page', `${insetFor(this.getBoundingClientRect())}px`);
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
